import { Box, Button, Typography } from '@mui/material'

import { appColors } from 'utils'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

const placements = {
  bottom: { flexDirection: 'column' },
  top: { flexDirection: 'column-reverse' },
  right: { flexDirection: 'row' },
  left: { flexDirection: 'row-reverse' },
}

const RibbonButton = ({
  icon,
  label,
  iconColor,
  disabled = false,
  active = false,
  onClick,
  height = 40,
  minWidth = 'auto',
  labelPlacement = 'bottom',
  labelMaxWidth,
  iconSize,
  iconBoxSize,
  gap = 0.25,
  padding = '4px 8px',
  fullWidth = false,
  textTransform = 'capitalize',
  labelProps,
  buttonProps,
  badge = null,
  variant = 'default', // 'default' | 'bordered'

  reserveLabelSpace = false,
  menuIcon = null,
  menuIconOnHover = false,
  menuOpen = false,
}) => {
  const { theme, ribbonStyle } = useInnovaTheme()

  const baseText = iconColor || '#ffffff'
  const color = active ? (theme === 'dark' ? appColors.itemTextColor : baseText) : baseText
  const iconOnly = icon && (!label || labelPlacement === 'hidden')

  const resolvedIconSize =
    iconSize ?? (iconOnly ? Math.max(24, Math.round(height * 0.5)) : height <= 32 ? 16 : height <= 45 ? 18 : 32)
  const resolvedIconBox = iconBoxSize ?? resolvedIconSize + 6

  const isBordered = variant === 'bordered'
  const activeBg = ribbonStyle.backgroundColor
  const hoverBg = active
    ? activeBg
    : isBordered
      ? ribbonStyle.backgroundColor
      : theme === 'dark'
        ? `${baseText}14`
        : `${baseText}10`

  const { flexDirection } = placements[labelPlacement] || placements.bottom
  const isColumnLayout = flexDirection === 'column' || flexDirection === 'column-reverse'

  return (
    <Box
      sx={{ position: 'relative', display: fullWidth ? 'block' : 'inline-block', width: fullWidth ? '100%' : 'auto' }}>
      <Button
        disabled={disabled}
        onClick={onClick}
        {...buttonProps}
        sx={{
          height: 'fit-content',
          minHeight: height,
          minWidth: iconOnly ? height : minWidth,
          width: fullWidth ? '100%' : iconOnly ? height : 'auto',
          p: padding,
          pb: isColumnLayout ? '10px' : padding,

          display: 'flex',
          flexDirection,
          alignItems: 'center',
          justifyContent: fullWidth ? 'flex-start' : 'center',
          flexShrink: 0,
          gap,

          color,
          textTransform: 'none',
          lineHeight: 1,
          borderRadius: 1.5,
          border: '1px solid',
          boxSizing: 'border-box',
          borderColor: active
            ? ribbonStyle.activeBorderColor
            : isBordered
              ? ribbonStyle.defaultBorderColor
              : 'transparent',
          backgroundColor: active ? activeBg : isBordered ? ribbonStyle.backgroundColor : 'transparent',
          transform: active ? 'translateY(-1px)' : 'none',
          boxShadow: active ? `0 2px 4px ${ribbonStyle.activeBorderColor}30` : 'none',
          transition: 'all 0.18s ease',
          whiteSpace: 'nowrap',
          overflow: 'hidden',

          '&:hover': {
            backgroundColor: hoverBg,
            borderColor: active
              ? ribbonStyle.activeBorderColor
              : isBordered
                ? theme === 'dark'
                  ? '#3b4754'
                  : '#c3ccd6'
                : `${baseText}40`,
            boxShadow: active
              ? `inset 0 0 0 1px ${ribbonStyle.activeBorderColor}30, 0 0 0 1px ${ribbonStyle.activeBorderColor}18`
              : 'none',
            ...(menuIconOnHover && {
              '& .ribbon-button-menu-icon': { opacity: 1 },
            }),
          },

          '&.Mui-disabled': { opacity: 0.45 },

          ...(buttonProps?.sx || {}),
        }}>
        {icon && (
          <Box
            sx={{
              width: resolvedIconBox,
              height: resolvedIconBox,
              minWidth: resolvedIconBox,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color,
              '& svg': { width: resolvedIconSize, height: resolvedIconSize, display: 'block', fill: 'currentColor' },
              '& img': { width: resolvedIconSize, height: resolvedIconSize, objectFit: 'contain' },
            }}>
            {icon}
          </Box>
        )}

        {labelPlacement !== 'hidden' && (label || reserveLabelSpace) && (
          <Typography
            fontSize={height < 40 ? { xs: 11, xl: 12 } : { xs: 13, xl: 14 }}
            {...labelProps}
            sx={{
              color,
              lineHeight: 1.05,
              textTransform,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: labelMaxWidth ?? (labelPlacement === 'bottom' || labelPlacement === 'top' ? 'none' : 110),
              ...(reserveLabelSpace && { minHeight: '14px', visibility: label ? 'visible' : 'hidden' }),
              ...(labelProps?.sx || {}),
            }}>
            {label || (reserveLabelSpace ? 'placeholder' : null)}
          </Typography>
        )}

        {menuIcon && (
          <Box
            className='ribbon-button-menu-icon'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color,
              '& svg': { width: 16, height: 16 },
              ...(menuIconOnHover && {
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                opacity: menuOpen ? 1 : 0,
                pointerEvents: 'none',
                transition: 'opacity 0.18s ease',
              }),
            }}>
            {menuIcon}
          </Box>
        )}

        {badge?.show && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 16,
              height: 16,
              px: 0.5,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 600,
              background: badge.color || 'orange',
              color: '#fff',
              pointerEvents: 'none',
              boxShadow: '0 0 0 2px rgba(0,0,0,0.15)',
            }}>
            {badge.msg}
          </Box>
        )}
      </Button>
    </Box>
  )
}

export default RibbonButton
